// 📍 src/pages/Economics.jsx
//
// What we EARN vs what we SPEND. Revenue = active-subscription MRR.
// Spend = real Claude/OpenAI token cost from api_logs (streaming now logs
// real tokens too). Backed by adminSpendAnalytics — today / 7d / 30d / year
// plus the top spenders so a single runaway user is obvious at a glance.

import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PageHeader, Card, StatCard, Spinner, Table, Badge } from '../components/UI';

const usd = (n) => '$' + (Number(n) || 0).toFixed(2);
const compact = (n) => {
  n = Number(n) || 0;
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
};
const tierColor = { free: '#8A8F98', student: '#3B82F6', premium: '#008751',
  scholar: '#D4A500', family: '#A855F7', school: '#EC4899' };
const tierLabel = { free: 'Free', student: 'Student', premium: 'Oga', scholar: 'Odogwu', family: 'Family', school: 'School' };

export default function Economics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminSpendAnalytics');
      const res = await fn({});
      setData(res.data);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  if (err) return <Card><div style={{ color: '#E5484D' }}>⚠ {err}</div></Card>;
  if (!data) return null;

  const { revenue, spend, tokens, margin, topSpenders } = data;
  const marginPct = revenue.month > 0 ? Math.round((margin.month / revenue.month) * 100) : null;

  const spenderCols = [
    { key: 'rank', label: '#' },
    { key: 'uid', label: 'User' },
    { key: 'tier', label: 'Tier' },
    { key: 'calls', label: 'Calls (30d)' },
    { key: 'io', label: 'In / Out tokens' },
    { key: 'cost', label: 'Cost (30d)' },
    { key: 'rev', label: 'Pays/mo' },
    { key: 'net', label: 'Net' },
  ];
  const spenderRows = (topSpenders || []).map((s, i) => {
    const net = (s.revenue || 0) - (s.cost || 0);
    return {
      rank: i + 1,
      uid: <span title={s.uid} style={{ fontFamily: 'monospace', fontSize: 12 }}>{(s.uid || '').slice(0, 10)}…</span>,
      tier: <Badge label={tierLabel[s.tier] || s.tier} color={tierColor[s.tier] || '#888'} />,
      calls: s.calls,
      io: `${compact(s.inTok)} / ${compact(s.outTok)}`,
      cost: <span style={{ color: '#E5484D', fontWeight: 700 }}>{usd(s.cost)}</span>,
      rev: usd(s.revenue),
      net: <span style={{ color: net >= 0 ? '#30A46C' : '#E5484D', fontWeight: 700 }}>{usd(net)}</span>,
    };
  });

  return (
    <div>
      <PageHeader title="💰 Economics" subtitle="What we earn vs what the AI costs us — live from token logs" />

      {/* headline: earn vs spend vs margin */}
      <div style={grid4}>
        <StatCard label="Revenue (MRR)" value={usd(revenue.mrr)} sub={`${usd(revenue.annualized)}/yr`} icon="📈" color="#30A46C" />
        <StatCard label="AI Spend · 30 days" value={usd(spend.month)} sub={`${usd(spend.year)} last yr`} icon="🔥" color="#E5484D" />
        <StatCard label="Gross Margin · month" value={usd(margin.month)}
          sub={marginPct != null ? `${marginPct}% of revenue` : '—'} icon="🏦"
          color={margin.month >= 0 ? '#30A46C' : '#E5484D'} />
        <StatCard label="Paying subscribers" value={Object.values(revenue.byTier || {}).reduce((a, b) => a + b, 0)}
          sub={Object.entries(revenue.byTier || {}).map(([t, n]) => `${n} ${tierLabel[t] || t}`).join(' · ') || 'none yet'}
          icon="👑" color="#D4A500" />
      </div>

      {/* spend over time */}
      <Card title="AI spend over time">
        <div style={grid4}>
          <MiniStat label="Today" value={usd(spend.today)} tok={tokens.today} />
          <MiniStat label="7 days" value={usd(spend.week)} tok={tokens.week} />
          <MiniStat label="30 days" value={usd(spend.month)} tok={tokens.month} />
          <MiniStat label="This year" value={usd(spend.year)} tok={null} note="fills nightly" />
        </div>
      </Card>

      {/* revenue over time */}
      <Card title="Revenue (recurring)">
        <div style={grid4}>
          <MiniStat label="Per day" value={usd(revenue.today)} />
          <MiniStat label="Per week" value={usd(revenue.week)} />
          <MiniStat label="Per month" value={usd(revenue.month)} />
          <MiniStat label="Per year" value={usd(revenue.annualized)} />
        </div>
      </Card>

      {/* who costs the most */}
      <Card title="Top spenders (last 30 days) — spot a runaway user instantly">
        <Table columns={spenderCols} data={spenderRows} />
        {spenderRows.length === 0 && <div style={{ color: '#8A8F98', padding: 16 }}>No usage logged yet in this window.</div>}
      </Card>

      <div style={{ color: '#8A8F98', fontSize: 12, marginTop: 12 }}>
        Cost is estimated from logged input/output tokens × model price (Haiku $1/$5 · Sonnet $3/$15 · Fable $10/$50 per 1M).
        Streaming chat now logs real tokens — figures grow more accurate from 2026-07-28 onward.
      </div>
    </div>
  );
}

function MiniStat({ label, value, tok, note }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E6E8EB', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, color: '#8A8F98', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, margin: '4px 0' }}>{value}</div>
      {tok && <div style={{ fontSize: 12, color: '#6E7681' }}>{compact(tok.in)} in · {compact(tok.out)} out</div>}
      {note && <div style={{ fontSize: 11, color: '#B0B4BA' }}>{note}</div>}
    </div>
  );
}

const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 16 };
